
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/es';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 任务日期（Task Date）可选范围规则 —— 全部基于「仓库当地时间」。
 *
 * - 仓库当地 00:00~09:59：只能选当日（后端同样拦截 10 点前发次日）
 * - 仓库当地 10:00~20:59：可选当日或次日
 * - 仓库当地 21:00~23:59：只能选次日（仅前端拦截）
 *
 * 时间基准取服务器按仓库时区换算的 warehouseCurrentTime（getWarehousePlanningEntryConfigByWarehouseId 返回），
 * 以防被篡改的设备时钟把任务发到过去/未来；拿不到时降级为设备时间 dayjs()。
 */

/** 上午截止点：此前（仓库当地 <10 点）不得发布次日 */
export const MORNING_CUTOFF_HOUR = 10;
/** 傍晚截止点：此后（仓库当地 >=21 点）只能发布次日 */
export const EVENING_DEFAULT_HOUR = 21;

/**
 * 解析仓库当前时间。
 * warehouseCurrentTime 为 ISO_LOCAL_DATE_TIME（无偏移，已是仓库当地墙上时间），直接当仓库当前时间用；
 * 缺失/非法时降级为设备时间。
 */
export const resolveWarehouseNow = (warehouseCurrentTime?: string | null): Dayjs => {
    if (warehouseCurrentTime) {
        const d = dayjs(warehouseCurrentTime);
        if (d.isValid()) return d;
    }
    return dayjs();
};

/** 是否处于「只能发当日」时段（仓库当地 10 点前） */
export const isMorningOnlyTodayWindow = (whsNow: Dayjs): boolean =>
    whsNow.hour() < MORNING_CUTOFF_HOUR;

/** 是否处于「只能发次日」时段（仓库当地 21 点后） */
export const isEveningDefaultTomorrowWindow = (whsNow: Dayjs): boolean =>
    whsNow.hour() >= EVENING_DEFAULT_HOUR;

/** DatePicker disabledDate：仅当日/次日可选；仓库当地 10 点前禁次日，21 点后禁当日 */
export const isTaskDateDisabled = (current: Dayjs, whsNow: Dayjs): boolean => {
    if (!current) return false;
    const today = whsNow.startOf('day');
    const tomorrow = today.add(1, 'day');
    if (current.isBefore(today, 'day') || current.isAfter(tomorrow, 'day')) return true;
    if (isMorningOnlyTodayWindow(whsNow) && current.isSame(tomorrow, 'day')) return true;
    if (isEveningDefaultTomorrowWindow(whsNow) && current.isSame(today, 'day')) return true;
    return false;
};

/** 点「确认」时按最新仓库时间复核所选日期是否仍可选（跨时段，如 20:58 选当日、21:01 才确认） */
export const isTaskDateSelectable = (taskDate: Dayjs | null | undefined, whsNow: Dayjs): boolean =>
    !!taskDate && !isTaskDateDisabled(taskDate, whsNow);

/** 界面语言 → dayjs locale；其余语言按英文 */
const DAYJS_LOCALE: Record<string, string> = { zh: 'zh-cn', en: 'en', es: 'es' };

/**
 * 日期 + 星期，格式跟随后台语言：中文「2026-10-16 星期五」，英文「2026-10-16 Fri」，西语「2026-10-16 vie.」。
 *
 * @param date 任务日期
 * @param language 界面语言（如 zh / zh-CN / en / es）
 */
export const formatTaskDateWithWeekday = (date: Dayjs, language?: string | null): string => {
    const lang = (language || 'en').toLowerCase().split('-')[0];
    const locale = DAYJS_LOCALE[lang] ?? 'en';
    return date.locale(locale).format(lang === 'zh' ? 'YYYY-MM-DD dddd' : 'YYYY-MM-DD ddd');
};
